<script lang="ts">
  import FooterCategorySubcomponent from './FooterCategorySubcomponent/FooterCategorySubcomponent.svelte';
  import FooterLanguageDisclaimer from './FooterLanguageDisclaimer.svelte';
  import FooterLinkSubcomponent from './FooterLinkSubcomponent/FooterLinkSubcomponent.svelte';
  import type { FooterComponentProps } from './types';

  export let navigationCategory: FooterComponentProps['navigationCategory'];
  export let globeIcon: FooterComponentProps['globeIcon'];
  export let countryName: FooterComponentProps['countryName'];
  export let languageName: FooterComponentProps['languageName'];
  export let copyright: FooterComponentProps['copyright'];
  export let address: FooterComponentProps['address'];
  export let license: FooterComponentProps['license'];
  export let languageDisclaimer: FooterComponentProps['languageDisclaimer'];
  export let legalNavigationLinks: FooterComponentProps['legalNavigationLinks'];
  export let openLocaleSelectorModal: () => void;

  const SMALL_BREAKPOINT = 480;
  let width = 0;
  let isFolded = true;

  $: isLessThanSmallBreakpoint = width < SMALL_BREAKPOINT;
</script>

<svelte:window bind:innerWidth={width} />

<nav class="mainNav">
  <div class="navCategories">
    {#if navigationCategory}
      {#each navigationCategory as category}
        <FooterCategorySubcomponent {...category.props} />
      {/each}
    {/if}
  </div>
  <div class="navLinksDropdown">
    {#if isLessThanSmallBreakpoint}
      <button
        on:click={() => {
          isFolded = !isFolded;
        }}
      >
        <h6 class="caret" class:caret-flipped={!isFolded}>Legal</h6>
      </button>
    {:else}
      <h6>Legal</h6>
    {/if}
    <div class="navLinks" class:folded={isLessThanSmallBreakpoint && isFolded}>
      <ul class="navLinksList">
        {#each legalNavigationLinks ?? [] as legalLink}
          <FooterLinkSubcomponent id={legalLink.contentfulEntryId} locale={legalLink.locale} {...legalLink.props} />
        {/each}
        {#if license}
          <li><a href="https://sqcapitallicenses.com">{license}</a></li>
        {/if}
      </ul>
    </div>
  </div>
</nav>

<FooterLanguageDisclaimer>{@html languageDisclaimer}</FooterLanguageDisclaimer>

<!-- tertiary section -->
<nav class="tertiaryNav">
  <button id="locale-selector-trigger" on:click={openLocaleSelectorModal} data-modal="locale-selector-modal">
    <!-- eslint-disable-next-line svelte/no-at-html-tags - icon is imported from public-web-icons -->
    {@html globeIcon} &nbsp;{countryName} ({languageName})
  </button>
</nav>

<!-- copyright and notices -->
<div class="copyright">
  <div class="copyrightText">{copyright}</div>
  {#if address}
    <div class="address">{address}</div>
  {/if}
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/grid.scss' as grid;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/variables.scss' as vars;

  @layer standard-components {
    .mainNav {
      @include grid.auto;
      @include grid.space('none');
      @include grid.space(30, $at: 'small');

      margin-bottom: var(--space-60);
      text-align: center;

      @media (min-width: vars.$breakpoint-width-medium) {
        text-align: left;
      }

      .navCategories {
        @include grid.columns(1);
        @include grid.space(20);
        @include grid.fraction(1, 3, $at: 'medium');
      }

      .navLinksDropdown {
        @include grid.columns(1);
        @include grid.space(20);
        @include grid.fraction(2, 3, $at: 'medium');
      }

      button {
        border: none;
        background: none;
        color: inherit;
        cursor: pointer;
      }

      h6 {
        margin-top: 0;
        margin-bottom: var(--space-10);
        color: var(--color-gray-55);
      }

      .caret {
        position: relative;
      }

      .caret::after {
        content: '›';
        pointer-events: none;
        display: inline-block;
        margin-left: 0.5em;
        transition: transform var(--duration-quick) var(--easing-sharp);
        transform: rotate(90deg);
      }

      .caret-flipped::after {
        transform: translateX(-0.1875em) rotate(-90deg);
      }

      .navLinks {
        padding-bottom: var(--space-30);
      }

      .navLinksList {
        @include grid.columns(1);
        @include grid.space-col(20);
        @include grid.space-row(10);
      }

      .folded {
        overflow: hidden;
        height: 0;
        opacity: 0;
        padding-bottom: 0;
      }

      li {
        padding-left: 0;
      }
    }

    .tertiaryNav {
      @include grid.flex($at: 'extra-small');
      @include grid.space(20);
      @include grid.align('justify');

      padding-top: var(--space-20);
      padding-bottom: var(--space-20);
      margin-bottom: var(--space-20);
      border-top: 1px solid var(--color-border-transparent);
      border-bottom: 1px solid var(--color-border-transparent);
      flex-shrink: 0;

      a {
        color: white;
      }

      button {
        border: none;
        background: none;
        color: white;
        cursor: pointer;
        font-family: var(--font-family-base);
        font-size: 100%;
        font-weight: var(--font-weight-medium);
        text-decoration: none;
      }

      button:focus {
        outline: none;
        box-shadow: none;
      }
    }

    .copyright {
      @include grid.columns(1);
      @include grid.columns(3, $at: large);
      @include grid.space-col(20);
      @include grid.space-row('none');

      .copyrightText {
        @include grid.columns(1);
        @include grid.space(20);
        @include grid.fraction(1, 3, $at: large);
      }

      .address {
        @include grid.columns(1);
        @include grid.space(20);
        @include grid.fraction(2, 3, $at: large);
      }
    }
    @media (min-width: vars.$breakpoint-width-large) {
      .address {
        text-align: right;
      }
    }
  }
</style>
